import { useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  X,
  Ban,
  Eye,
  BookText,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  useAsientos,
  useAsiento,
  useCreateAsiento,
  useAnularAsiento,
  useCuentas,
} from "../api";
import { useAccountingStore } from "../store";
import { AccountingHeader } from "../components/AccountingHeader";
import type { Asiento } from "../types";
import { Pagination, pageSlice } from "@modules/billing/components/Pagination";

function eur(n: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

interface ApunteLocal {
  cuenta_codigo: string;
  descripcion: string;
  debe: number;
  haber: number;
}

const EMPTY_APUNTE: ApunteLocal = { cuenta_codigo: "", descripcion: "", debe: 0, haber: 0 };

export function DiarioPage() {
  const { emisorId, ejercicio } = useAccountingStore();
  const { data: asientos, isLoading } = useAsientos(emisorId, { ejercicio });
  const { data: cuentas } = useCuentas(emisorId, { activa: true });

  const createMut = useCreateAsiento();
  const anularMut = useAnularAsiento();

  const [detalleId, setDetalleId] = useState<number | null>(null);
  const { data: detalle } = useAsiento(emisorId, detalleId);

  const [creating, setCreating] = useState(false);
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [concepto, setConcepto] = useState("");
  const [referencia, setReferencia] = useState("");
  const [apuntes, setApuntes] = useState<ApunteLocal[]>([
    { ...EMPTY_APUNTE },
    { ...EMPTY_APUNTE },
  ]);
  const [formError, setFormError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const paged = useMemo(() => pageSlice(asientos, page, pageSize), [asientos, page, pageSize]);

  const totales = useMemo(() => {
    const d = apuntes.reduce((a, x) => a + (Number(x.debe) || 0), 0);
    const h = apuntes.reduce((a, x) => a + (Number(x.haber) || 0), 0);
    return {
      debe: Math.round(d * 100) / 100,
      haber: Math.round(h * 100) / 100,
      descuadre: Math.round((d - h) * 100) / 100,
      cuadra: Math.abs(d - h) <= 0.01 && d > 0,
    };
  }, [apuntes]);

  function openCreate() {
    setFecha(new Date().toISOString().slice(0, 10));
    setConcepto("");
    setReferencia("");
    setApuntes([{ ...EMPTY_APUNTE }, { ...EMPTY_APUNTE }]);
    setFormError(null);
    setCreating(true);
  }

  function updateApunte(idx: number, patch: Partial<ApunteLocal>) {
    setApuntes((aps) => aps.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    setFormError(null);
    if (!totales.cuadra) {
      setFormError(
        `El asiento no cuadra: Debe ${eur(totales.debe)} ≠ Haber ${eur(totales.haber)}.`,
      );
      return;
    }
    try {
      await createMut.mutateAsync({
        emisor_id: emisorId,
        fecha,
        concepto,
        referencia: referencia || null,
        apuntes: apuntes
          .filter((a) => a.cuenta_codigo && (a.debe > 0 || a.haber > 0))
          .map((a) => ({
            cuenta_codigo: a.cuenta_codigo,
            descripcion: a.descripcion || null,
            debe: Number(a.debe),
            haber: Number(a.haber),
          })),
      });
      setCreating(false);
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const first = e2?.response?.data?.errors
        ? Object.values(e2.response.data.errors).flat()[0]
        : null;
      setFormError(first ?? e2?.response?.data?.message ?? "Error guardando el asiento.");
    }
  }

  function anular(a: Asiento) {
    if (!emisorId) return;
    if (confirm(`¿Anular el asiento #${a.id}? Se creará un contraasiento.`)) {
      anularMut.mutate({ id: a.id, emisorId });
    }
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <AccountingHeader
        title="Contabilidad"
        subtitle="Libro diario: asientos contables del ejercicio."
        right={
          <button
            onClick={openCreate}
            disabled={!emisorId}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white px-4 py-2 font-bold text-sm shadow-soft transition self-end"
          >
            <Plus size={16} /> Nuevo asiento
          </button>
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {asientos && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-300px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold w-16">#</th>
                  <th className="text-left px-4 py-3 font-bold w-28">Fecha</th>
                  <th className="text-left px-4 py-3 font-bold">Concepto</th>
                  <th className="text-left px-4 py-3 font-bold w-32">Referencia</th>
                  <th className="text-center px-4 py-3 font-bold w-28">Estado</th>
                  <th className="text-right px-4 py-3 font-bold w-32">Debe</th>
                  <th className="text-right px-4 py-3 font-bold w-32">Haber</th>
                  <th className="text-right px-4 py-3 font-bold w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {asientos.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      No hay asientos en {ejercicio}.
                    </td>
                  </tr>
                )}
                {paged.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 font-mono font-bold text-slate-900">{a.id}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">
                      {new Date(a.fecha).toLocaleDateString("es-ES")}
                    </td>
                    <td className="px-4 py-2.5 text-sm">{a.concepto}</td>
                    <td className="px-4 py-2.5 text-sm font-mono text-slate-500">
                      {a.referencia ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {a.is_anulado ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border bg-rose-50 text-rose-700 border-rose-200">
                          Anulado
                        </span>
                      ) : a.is_contraasiento ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border bg-amber-50 text-amber-700 border-amber-200">
                          Contraasiento
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border bg-emerald-50 text-emerald-700 border-emerald-200">
                          Activo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold">
                      {eur(a.total_debe ?? 0)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold">
                      {eur(a.total_haber ?? 0)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setDetalleId(a.id)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                          title="Ver detalle"
                        >
                          <Eye size={15} />
                        </button>
                        {!a.is_anulado && !a.is_contraasiento && (
                          <button
                            onClick={() => anular(a)}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                            title="Anular (crea contraasiento)"
                          >
                            <Ban size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={asientos.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* Modal detalle */}
      {detalleId && detalle && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-auto">
            <header className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                  <BookText size={20} className="text-brand-700" /> Asiento #{detalle.id}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {new Date(detalle.fecha).toLocaleDateString("es-ES")} · {detalle.concepto}
                  {detalle.referencia && ` · Ref: ${detalle.referencia}`}
                </p>
              </div>
              <button
                onClick={() => setDetalleId(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </header>

            {detalle.is_anulado && (
              <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm font-bold">
                Anulado por el asiento #{detalle.anulado_por_asiento_id}
              </div>
            )}
            {detalle.is_contraasiento && (
              <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 text-sm font-bold">
                Este es el contraasiento que anula al #{detalle.es_contraasiento_de}
              </div>
            )}

            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Cuenta</th>
                  <th className="text-left px-3 py-2 font-bold">Descripción</th>
                  <th className="text-right px-3 py-2 font-bold w-28">Debe</th>
                  <th className="text-right px-3 py-2 font-bold w-28">Haber</th>
                </tr>
              </thead>
              <tbody>
                {detalle.apuntes?.map((ap) => (
                  <tr key={ap.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono">
                      <span className="font-bold">{ap.cuenta_codigo}</span>
                      {ap.cuenta_nombre && (
                        <span className="text-slate-500 text-xs block">{ap.cuenta_nombre}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{ap.descripcion ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {ap.debe > 0 ? eur(ap.debe) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {ap.haber > 0 ? eur(ap.haber) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-bold border-t-2 border-slate-200">
                <tr>
                  <td colSpan={2} className="px-3 py-2 text-right uppercase text-xs">
                    Totales
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {eur(detalle.apuntes?.reduce((s, a) => s + a.debe, 0) ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {eur(detalle.apuntes?.reduce((s, a) => s + a.haber, 0) ?? 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Modal crear */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form
            onSubmit={save}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 max-h-[92vh] overflow-auto"
          >
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nuevo asiento</h2>
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </header>

            {formError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Fecha *
                </label>
                <input
                  required
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Concepto *
                </label>
                <input
                  required
                  type="text"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Referencia
                </label>
                <input
                  type="text"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Apuntes */}
            <div className="border border-slate-200 rounded-xl overflow-hidden mb-3">
              <div className="bg-slate-50 px-4 py-2 flex items-center justify-between border-b border-slate-200">
                <span className="font-bold text-sm text-slate-700">Apuntes</span>
                <button
                  type="button"
                  onClick={() => setApuntes((aps) => [...aps, { ...EMPTY_APUNTE }])}
                  className="text-xs font-bold text-brand-700 hover:text-brand-900 flex items-center gap-1"
                >
                  <Plus size={13} /> Añadir línea
                </button>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-2 py-2 font-bold">Cuenta</th>
                    <th className="text-left px-2 py-2 font-bold">Descripción</th>
                    <th className="text-right px-2 py-2 font-bold w-28">Debe</th>
                    <th className="text-right px-2 py-2 font-bold w-28">Haber</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {apuntes.map((ap, idx) => (
                    <tr key={idx} className="border-t border-slate-50">
                      <td className="px-2 py-1.5">
                        <input
                          list="cuentas-list"
                          value={ap.cuenta_codigo}
                          onChange={(e) => updateApunte(idx, { cuenta_codigo: e.target.value })}
                          placeholder="código"
                          className="w-full rounded border border-slate-200 px-2 py-1 text-sm font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={ap.descripcion}
                          onChange={(e) => updateApunte(idx, { descripcion: e.target.value })}
                          className="w-full rounded border border-slate-200 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={ap.debe || ""}
                          onChange={(e) =>
                            updateApunte(idx, {
                              debe: Number(e.target.value) || 0,
                              haber: 0,
                            })
                          }
                          className="w-full rounded border border-slate-200 px-2 py-1 text-sm text-right font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={ap.haber || ""}
                          onChange={(e) =>
                            updateApunte(idx, {
                              haber: Number(e.target.value) || 0,
                              debe: 0,
                            })
                          }
                          className="w-full rounded border border-slate-200 px-2 py-1 text-sm text-right font-mono"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {apuntes.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setApuntes((aps) => aps.filter((_, i) => i !== idx))}
                            className="text-rose-600 hover:text-rose-800"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <datalist id="cuentas-list">
                {cuentas?.map((c) => (
                  <option key={c.id} value={c.codigo}>
                    {c.codigo} · {c.nombre}
                  </option>
                ))}
              </datalist>
            </div>

            {/* Indicador de cuadre */}
            <div
              className={`flex items-center justify-between rounded-xl px-4 py-3 mb-4 ${
                totales.cuadra
                  ? "bg-emerald-50 border border-emerald-200"
                  : "bg-amber-50 border border-amber-200"
              }`}
            >
              <div className="flex items-center gap-2 font-bold text-sm">
                {totales.cuadra ? (
                  <>
                    <CheckCircle2 size={18} className="text-emerald-600" />
                    <span className="text-emerald-800">El asiento cuadra</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={18} className="text-amber-600" />
                    <span className="text-amber-800">
                      Descuadre: {eur(totales.descuadre)}
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-6 text-sm font-mono font-bold">
                <span>
                  Debe: <span className="text-slate-900">{eur(totales.debe)}</span>
                </span>
                <span>
                  Haber: <span className="text-slate-900">{eur(totales.haber)}</span>
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || !totales.cuadra}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar asiento
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
